import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';
import { Spinner } from './spinner';
import { Icon } from './icon';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 border font-body font-medium',
    'transition-colors duration-fast ease-smooth',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-white',
    'disabled:pointer-events-none disabled:opacity-40',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground border-primary hover:bg-primary-hover active:bg-primary/80',
        default:
          'bg-white text-foreground border-border hover:border-primary hover:text-primary',
        danger:
          'bg-white text-destructive border-destructive hover:bg-destructive/5 active:bg-destructive/10',
        dashed:
          'bg-white text-foreground border-border border-dashed hover:border-primary hover:text-primary',
        link:
          'bg-transparent text-primary border-transparent hover:text-primary-hover underline-offset-4 hover:underline p-0! h-auto!',
        ghost:
          'bg-transparent text-foreground border-transparent hover:bg-primary/5 hover:text-primary',
      },
      size: {
        large: 'h-12 px-6 text-sm rounded-md',
        default: 'h-10 px-4 text-xs rounded-sm',
        small: 'h-8 px-3 text-xs rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  shape?: 'circle' | 'circle-outline' | 'default' | 'round';
  icon?: React.ReactNode;
  htmlType?: 'submit' | 'reset' | 'button';
  /** @deprecated Use variant instead */
  type?: 'primary' | 'default' | 'danger' | 'dashed' | 'link' | 'ghost';
}

const ButtonBase = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      type,
      htmlType,
      size = 'default',
      block = false,
      loading = false,
      shape,
      icon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedVariant = (variant || type || 'default') as ButtonVariant;

    return (
      <button
        ref={ref}
        type={htmlType || 'button'}
        className={cn(
          buttonVariants({ variant: resolvedVariant, size }),
          shape === 'circle' && 'rounded-full px-0! aspect-square',
          block && 'w-full',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Spinner size="small" className="mr-1" />}
        {!loading && icon && (
          typeof icon === 'string'
            ? <Icon type={icon} size={size === 'small' ? 12 : size === 'large' ? 18 : 14} />
            : icon
        )}
        {children}
      </button>
    );
  }
);
ButtonBase.displayName = 'Button';

/** Button group for related actions */
export function ButtonGroup({
  children,
  size,
  className,
  ...props
}: {
  children: React.ReactNode;
  size?: 'large' | 'default' | 'small';
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('inline-flex', className)}
      role="group"
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement<ButtonProps>(child)) {
          return React.cloneElement(child, {
            size: size || child.props.size,
            className: cn(
              'rounded-none first:rounded-l-sm last:rounded-r-sm',
              '-ml-px first:ml-0',
              child.props.className
            ),
          });
        }
        return child;
      })}
    </div>
  );
}

// Compound component for antd compat: Button.Group
export const Button = Object.assign(ButtonBase, { Group: ButtonGroup });
